<template>
	<div class="ks-dialog-wrapper" :class="{ 'ks-dialog-wrapper-inline': hasActivator }">
		<div v-if="hasActivator" class="ks-dialog-activator">
			<slot name="activator" :on="activatorListeners"></slot>
		</div>
	</div>
</template>

<script>
import { popupMixin, getPopupContainer, wasPressedOn } from "@/lib/popupStack"

export default {
	name: "KDialog",
	mixins: [popupMixin],
	props: {
		value: {
			type: Boolean,
			default: false
		},
		width: {
			type: [String, Number],
			default: "auto"
		},
		maxWidth: {
			type: [String, Number],
			default: "none"
		},
		fullscreen: {
			type: Boolean,
			default: false
		},
		persistent: {
			type: Boolean,
			default: false
		},
		scrollable: {
			type: Boolean,
			default: false
		},
		noClickAnimation: {
			type: Boolean,
			default: false
		},
		contentClass: {
			type: String,
			default: ""
		},
		transition: {
			type: [String, Boolean],
			default: "ks-dialog-transition"
		},
		hideOverlay: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			isActive: this.value,
			overlayEl: null,
			dialogEl: null,
			contentVm: null,
			stackZIndex: null
		}
	},
	computed: {
		hasActivator () {
			return !!this.$slots.activator
		},
		activatorListeners () {
			var vm = this
			return {
				click (e) {
					vm.isActive = !vm.isActive
				}
			}
		},
		computedTransition () {
			if (this.transition === false) return ""
			if (this.transition === "dialog-bottom-transition") return "ks-dialog-bottom-transition"
			if (this.transition === "dialog-transition") return "ks-dialog-transition"
			return "ks-dialog-transition"
		},
		isBottomDialog () {
			return this.transition === "dialog-bottom-transition"
		},
		overlayClasses () {
			return {
				"ks-dialog-overlay-no-click": this.noClickAnimation,
				"ks-dialog-overlay-bottom": this.isBottomDialog
			}
		},
		dialogClasses () {
			var classes = {
				"ks-dialog-fullscreen": this.fullscreen,
				"ks-dialog-scrollable": this.scrollable
			}
			if (this.contentClass) {
				classes[this.contentClass] = true
			}
			return classes
		},
		dialogStyles () {
			if (this.fullscreen) return {}
			var styles = {}
			if (this.width && this.width !== "auto") {
				styles.width = this.toPx(this.width)
			}
			if (this.maxWidth && this.maxWidth !== "none") {
				styles.maxWidth = this.toPx(this.maxWidth)
			}
			return styles
		}
	},
	watch: {
		value (val) {
			this.isActive = val
		},
		isActive (val) {
			this.$emit("input", val)
			if (val) {
				this.openOverlay()
			} else {
				this.closeOverlay()
			}
		},
		fullscreen (newVal, oldVal) {
			if (!this.overlayEl) return
			this.destroyOverlay();
			this.createOverlay();
		},
		maxWidth (newVal, oldVal) {
			if (this.dialogEl) {
				this.dialogEl.style.maxWidth = this.toPx(newVal)
			}
		},
	},
	mounted () {
		if (this.isActive) {
			this.openOverlay()
		}
	},
	beforeDestroy () {
		this.destroyOverlay()
	},
	updated () {
		// The slot content is rendered in a separate instance (contentVm) whose render function
		// reads the non-reactive this.$slots.default. Re-render it when the parent updates the slots.
		if (this.contentVm) this.contentVm.$forceUpdate()
	},
	methods: {
		toPx (val) {
			if (typeof val === "number") return val + "px"
			if (String(val).match(/^\d+$/)) return val + "px"
			return val
		},
		onOverlayClick () {
			if (this.persistent) {
				this.animateClick()
				return
			}
			this.isActive = false
		},
		onPopupEscape () {
			if (this.persistent) {
				this.animateClick()
				return
			}
			this.isActive = false
		},
		popupElements () {
			return [this.overlayEl]
		},
		animateClick () {
			if (this.noClickAnimation) return
			var el = this.dialogEl
			if (!el) return
			el.classList.add("ks-dialog-shake")
			setTimeout(() => {
				el.classList.remove("ks-dialog-shake")
			}, 150)
		},
		activatorClick () {
			this.isActive = !this.isActive
		},
		openOverlay () {
			this.stackZIndex = this.registerPopup({ lockScroll: true })
			this.createOverlay()
			this.$nextTick(() => {
				this.$emit("open")
			})
		},
		closeOverlay () {
			this.unregisterPopup()
			this.destroyOverlay()
			this.$emit("close")
		},
		createOverlay () {
			if (this.overlayEl) return
			var vm = this

			// Create overlay container
			var overlay = document.createElement("div")
			overlay.className = "ks-dialog-overlay"
			overlay.style.zIndex = this.stackZIndex
			if (this.isBottomDialog) overlay.classList.add("ks-dialog-overlay-bottom")
			if (this.fullscreen) overlay.classList.add("ks-dialog-overlay-fullscreen")
			if (this.noClickAnimation) overlay.classList.add("ks-dialog-overlay-no-click")
			overlay.addEventListener("click", function (e) {
				if (e.target === overlay && wasPressedOn(vm.popupHandle, overlay)) vm.onOverlayClick()
			})

			// Create dialog element
			var dialog = document.createElement("div")
			dialog.className = "ks-dialog"
			if (this.fullscreen) dialog.classList.add("ks-dialog-fullscreen")
			if (this.scrollable) dialog.classList.add("ks-dialog-scrollable")
			if (this.contentClass) dialog.classList.add(this.contentClass)
			var styles = this.dialogStyles
			for (var key in styles) {
				dialog.style[key] = styles[key]
			}
			dialog.addEventListener("click", function (e) {
				e.stopPropagation()
			})

			overlay.appendChild(dialog)
			getPopupContainer().appendChild(overlay)

			this.overlayEl = overlay
			this.dialogEl = dialog

			// Render slot content into dialog
			this.contentVm = new this.$root.constructor({
				parent: this,
				render: (h) => {
					var nodes = this.$slots.default
					return h("div", { style: { display: "contents" } }, nodes)
				}
			})
			this.contentVm.$mount()
			dialog.appendChild(this.contentVm.$el)

			// Trigger enter animation
			var transName = this.computedTransition
			if (transName) {
				overlay.classList.add(transName + "-enter")
				overlay.classList.add(transName + "-enter-active")
				void overlay.offsetWidth
				requestAnimationFrame(function () {
					overlay.classList.remove(transName + "-enter")
				})
				setTimeout(function () {
					overlay.classList.remove(transName + "-enter-active")
				}, 300)
			}
		},
		destroyOverlay () {
			if (!this.overlayEl) return
			var overlay = this.overlayEl
			var transName = this.computedTransition

			if (this.contentVm) {
				this.contentVm.$destroy()
				this.contentVm = null
			}

			if (transName) {
				// Let clicks pass through the overlay while it fades out
				overlay.style.pointerEvents = "none"
				overlay.classList.add(transName + "-leave-active")
				overlay.classList.add(transName + "-leave-to")
				setTimeout(function () {
					if (overlay.parentNode) overlay.parentNode.removeChild(overlay)
				}, 300)
			} else {
				if (overlay.parentNode) overlay.parentNode.removeChild(overlay)
			}

			this.overlayEl = null
			this.dialogEl = null
		}
	}
}
</script>

<style>
.ks-dialog-wrapper {
	display: contents;
}

.ks-dialog-wrapper-inline {
	display: inline;
}

.ks-dialog-activator {
	display: inline;
	cursor: pointer;
}

.ks-dialog-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 5, 0.2);

/*	var(--ks-background-color-op60);*/
	z-index: var(--ks-z-popup-base, 0);
	overflow-y: auto;
	padding: 24px;
	overflow: hidden;
}

.ks-dialog-overlay-bottom {
	align-items: flex-end;
	padding: 0;
}

.ks-dialog-overlay-bottom .ks-dialog {
	max-width: 100%;
	max-height: calc(100% - 20px);
	margin: 0px 0 0 0;
	border-radius: 4px 4px 0 0;
	width: 100%;
}

.ks-dialog-overlay-fullscreen {
	padding: 0;
	overflow: hidden;
}

.ks-dialog {
	position: relative;
	width: auto;
	max-width: 90%;
	max-height: 90vh;
	margin: auto;
/*	border-radius: 4px;
	background: var(--ks-background-color-light, #12202D);
	box-shadow: 0 11px 15px -7px rgba(0, 0, 0, 0.2),
	            0 24px 38px 3px rgba(0, 0, 0, 0.14),
	            0 9px 46px 8px rgba(0, 0, 0, 0.12);*/
	overflow: hidden;
	transition: transform 0.15s ease;
}

.ks-dialog-scrollable {
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.ks-dialog-scrollable > * {
	overflow-y: auto;
}

.ks-dialog-fullscreen {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	width: 100% !important;
	max-width: 100% !important;
	max-height: 100% !important;
	height: 100%;
	border-radius: 0;
	margin: 0;
}

.ks-dialog-shake {
	animation: ks-dialog-shake-anim 0.15s ease;
}

@keyframes ks-dialog-shake-anim {
	0%, 100% { transform: scale(1); }
	50% { transform: scale(1.01); }
}

.ks-dialog-transition-enter-active,
.ks-dialog-transition-leave-active {
	transition: opacity 0.1s ease;
}

.ks-dialog-transition-enter-active .ks-dialog,
.ks-dialog-transition-leave-active .ks-dialog {
	transition: transform 0.1s ease, opacity 0.1s ease;
}

.ks-dialog-transition-enter,
.ks-dialog-transition-leave-to {
	opacity: 0;
}

.ks-dialog-transition-enter .ks-dialog {
	transform: scale(0.9);
}

.ks-dialog-transition-leave-to .ks-dialog {
	transform: scale(0.9);
}

/* Bottom transition */
.ks-dialog-bottom-transition-enter-active,
.ks-dialog-bottom-transition-leave-active {
	transition: opacity 0.1s ease;
}

.ks-dialog-bottom-transition-enter-active .ks-dialog,
.ks-dialog-bottom-transition-leave-active .ks-dialog {
	transition: transform 0.1s ease;
}

.ks-dialog-bottom-transition-enter,
.ks-dialog-bottom-transition-leave-to {
	opacity: 0;
}

.ks-dialog-bottom-transition-enter .ks-dialog {
	transform: translateY(100%);
}

.ks-dialog-bottom-transition-leave-to .ks-dialog {
	transform: translateY(100%);
}
</style>
